import { PaginatedApiResponse } from 'types/apiResponse';
import { ApiController, LocationEndPoint, UserEndPoint } from 'config';
import { IUser, UserRoleId, IAssignedHrResponse, ILocation } from 'interfaces';
import { store } from 'redux/store';
import { get, patch, post } from '..';

export const setUserLocation = ({
  lastVisitedLocation,
}: Pick<IUser, 'lastVisitedLocation'>) => {
  const { user, role } = store.getState().auth;

  if (!user) throw new Error('User is not defined');
  if (!role) throw new Error('Role is not defined');

  return patch<IUser>({
    url: `/${ApiController.user}/${user.id}`,
    body: {
      login: user.login,
      roleId: role.id,

      lastVisitedLocationId: lastVisitedLocation
        ? lastVisitedLocation.id
        : null,

      assignedLocationIds: user.assignedLocations?.map((l) => l.id) ?? [],
    },
  });
};

export const getUserById = (userId: number) =>
  get<IUser>({ url: `/${ApiController.user}/${userId}` });

export const findEmployees = async (searchQuery: string): Promise<IUser[]> => {
  return get<IUser[]>({
    url: `/${ApiController.user}/${
      UserEndPoint.getAllEmployees
    }?searchQuery=${encodeURIComponent(searchQuery)}&page=0&size=1000`,
  });
};

export const changeUserRole = (userId: IUser['id'], roleId: UserRoleId) =>
  patch<IUser>({
    url: `/${ApiController.user}/${userId}/role?roleId=${roleId}`,
  });

export const patchAPlayer = (userId: IUser['id']) =>
  patch<IUser>({
    url: `/${ApiController.user}/${userId}/top-performer`,
  });

export const getAllHrUsers = (
  page = 0,
  size = 10,
  locationIds?: number[] | number,
) => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sort: 'id',
  });

  if (locationIds) {
    if (Array.isArray(locationIds)) {
      locationIds.forEach((id) => params.append('locationIds', String(id)));
    } else {
      params.append('locationIds', String(locationIds));
    }
  }

  return get<PaginatedApiResponse<IUser>>({
    url: `${ApiController.user}/${UserEndPoint.getAllHr}?${params}`,
  });
};

export const getAssignedHrsByLocation = (locationId: number) =>
  get<IAssignedHrResponse>({
    url: `/${ApiController.location}/${locationId}/${LocationEndPoint.assignedHrs}`,
  });

export const createAlumniUser = (body: {
  firstName: string;
  lastName: string;
  email: string;
  login: string;
}) =>
  post<IUser>({ url: `/${ApiController.user}/${UserEndPoint.alumni}`, body });

export const assignLocationsToUser = (
  userId: IUser['id'],
  locationIds: number[],
) =>
  patch<IUser>({
    url: `/${ApiController.user}/${userId}/assign-locations`,
    body: locationIds,
  });

export const unassignLocationsFromUser = (
  userId: IUser['id'],
  locationIds: number[],
) =>
  patch<IUser>({
    url: `/${ApiController.user}/${userId}/unassign-locations`,
    body: locationIds,
  });

export const getAllLocations = async (): Promise<ILocation[]> => {
  const response = await get<PaginatedApiResponse<ILocation>>({
    url: `/${ApiController.location}?page=0&size=1000`,
  });
  return response.content || [];
};
